

import React, {
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";

import {
    JWT
} from "../../../../constants/local-storage";

import "./EvaResourceLibrary.css";


//========================================================
// Eva Resource Library
//
// Displays resources returned by Eva's chat API.
//
// Props:
//   response - EvaChatResponse JSON object
//
// Responsibilities:
//   - Resource count indicator
//   - Left-hand resource drawer
//   - Group document snippets
//   - Open SharePoint PDF pages in a modal
//   - Open complete SharePoint documents
//   - Open web resources in new tabs
//   - Handle PDF authentication and cleanup
//
// All resource handling remains inside this component.
//========================================================

export default function EvaResourceLibrary({
    response
})
{

    //----------------------------------------------------
    // State
    //----------------------------------------------------

    const [drawerOpen, setDrawerOpen] =
        useState(false);

    const [selectedDocument, setSelectedDocument] =
        useState(null);

    const [pdfUrl, setPdfUrl] =
        useState(null);

    const [pdfLoading, setPdfLoading] =
        useState(false);

    const [pdfError, setPdfError] =
        useState(null);


    //----------------------------------------------------
    // References
    //----------------------------------------------------

    const pdfRequestRef = useRef(null);

    const pdfObjectUrlRef = useRef(null);


    //----------------------------------------------------
    // Resources
    //----------------------------------------------------

    const resources = useMemo(
        () =>
        {

            return Array.isArray(response?.resources)
                ? response.resources
                : [];

        },
        [response]
    );


    //----------------------------------------------------
    // Document Resources
    //----------------------------------------------------

    const documentResources = useMemo(
        () =>
        {

            const documents = new Map();

            resources
                .filter(
                    item => item.type === "DOCUMENT"
                )
                .forEach(
                    item =>
                    {

                        const sharePointId =
                            item.sharePointId;

                        const documentId =
                            item.documentId;

                        if (
                            documentId == null &&
                            !sharePointId
                        )
                        {
                            return;
                        }

                        const key =
                            documentId != null
                                ? String(documentId)
                                : String(sharePointId);

                        if (!documents.has(key))
                        {

                            documents.set(
                                key,
                                {
                                    key: key,
                                    documentId: documentId,
                                    sharePointId: sharePointId,
                                    title:
                                        item.title ||
                                        "Document",
                                    pages: []
                                }
                            );

                        }

                        const document =
                            documents.get(key);

                        if (
                            !document.sharePointId &&
                            sharePointId
                        )
                        {

                            document.sharePointId =
                                sharePointId;

                        }

                        document.pages.push(item);

                    }
                );


            return Array.from(documents.values());

        },
        [resources]
    );


    //----------------------------------------------------
    // Web Resources
    //----------------------------------------------------

    const webResources = useMemo(
        () =>
        {

            return resources.filter(
                item => item.type === "WEB"
            );

        },
        [resources]
    );


    //----------------------------------------------------
    // Counts
    //----------------------------------------------------

    const resourceCount =
        resources.length;

    const documentCount =
        documentResources.length;

    const webCount =
        webResources.length;


    //----------------------------------------------------
    // Release Existing PDF
    //----------------------------------------------------

    function releasePdf()
    {

        if (pdfRequestRef.current)
        {

            pdfRequestRef.current.abort();

            pdfRequestRef.current = null;

        }

        if (pdfObjectUrlRef.current)
        {

            URL.revokeObjectURL(
                pdfObjectUrlRef.current
            );

            pdfObjectUrlRef.current = null;

        }

    }


    //----------------------------------------------------
    // Close PDF
    //----------------------------------------------------

    function closePdf()
    {

        releasePdf();

        setPdfUrl(null);
        setPdfLoading(false);
        setPdfError(null);
        setSelectedDocument(null);

    }


    //----------------------------------------------------
    // Close Resource Drawer
    //----------------------------------------------------

    function closeDrawer()
    {

        closePdf();

        setDrawerOpen(false);

    }


    //----------------------------------------------------
    // Fetch PDF
    //
    // Uses the existing SharePoint controller.
    //
    // pageNumber:
    //   Number = single page PDF
    //   null   = complete PDF
    //----------------------------------------------------

    async function loadPdf(
        document,
        pageNumber
    )
    {

        releasePdf();

        setPdfUrl(null);
        setPdfError(null);
        setPdfLoading(true);

        const sharePointId =
            document.sharePointId;

        if (!sharePointId)
        {

            setPdfError(
                "This document does not have a SharePoint ID."
            );

            setPdfLoading(false);

            return;

        }


        //------------------------------------------------
        // Build SharePoint URL
        //------------------------------------------------

        const baseUrl =
            "/resolutionsoftwareplatform/api/sharepoint/file/" +
            encodeURIComponent(sharePointId);

        const endpoint =
            pageNumber != null
                ? baseUrl +
                    "/page/" +
                    pageNumber
                : baseUrl;


        //------------------------------------------------
        // Authenticated Request
        //------------------------------------------------

        const controller =
            new AbortController();

        pdfRequestRef.current =
            controller;


        try
        {

            const response = await fetch(
                endpoint,
                {
                    method: "GET",

                    headers:
                    {
                        "Authorization":
                            localStorage.getItem(JWT),

                        "Accept":
                            "application/pdf"
                    },

                    signal: controller.signal
                }
            );


            if (!response.ok)
            {

                throw new Error(
                    "Unable to retrieve PDF. HTTP " +
                    response.status
                );

            }


            //------------------------------------------------
            // Read PDF
            //------------------------------------------------

            const blob =
                await response.blob();


            if (controller.signal.aborted)
            {
                return;
            }


            //------------------------------------------------
            // Create Browser PDF URL
            //------------------------------------------------

            const objectUrl =
                URL.createObjectURL(
                    new Blob(
                        [blob],
                        {
                            type: "application/pdf"
                        }
                    )
                );


            if (controller.signal.aborted)
            {

                URL.revokeObjectURL(objectUrl);

                return;

            }


            pdfObjectUrlRef.current =
                objectUrl;

            setPdfUrl(objectUrl);

        }
        catch (error)
        {

            if (error.name === "AbortError")
            {
                return;
            }

            console.error(
                "Unable to open Eva PDF:",
                error
            );

            setPdfError(
                error.message ||
                "Unable to load document."
            );

        }
        finally
        {

            if (
                pdfRequestRef.current ===
                controller
            )
            {

                pdfRequestRef.current = null;

                setPdfLoading(false);

            }

        }

    }


    //----------------------------------------------------
    // Open Matching PDF Page
    //----------------------------------------------------

    function openDocument(
        document,
        page
    )
    {

        const pageNumber =
            page?.pageNumber || 1;

        const selection =
        {
            documentId:
                document.documentId,

            sharePointId:
                page?.sharePointId ||
                document.sharePointId,

            title:
                document.title,

            pageNumber:
                pageNumber,

            snippet:
                page?.snippet || "",

            fullDocument:
                false
        };


        setSelectedDocument(selection);

        loadPdf(
            selection,
            pageNumber
        );

    }


    //----------------------------------------------------
    // Open Complete PDF
    //----------------------------------------------------

    function openFullDocument(document)
    {

        const selection =
        {
            documentId:
                document.documentId,

            sharePointId:
                document.sharePointId,

            title:
                document.title,

            pageNumber:
                null,

            snippet:
                "",

            fullDocument:
                true
        };


        setSelectedDocument(selection);

        loadPdf(
            selection,
            null
        );

    }


    //----------------------------------------------------
    // Open Web Resource
    //----------------------------------------------------

    function openWebResource(resource)
    {

        if (!resource.url)
        {
            return;
        }


        try
        {

            const url =
                new URL(resource.url);


            if (
                url.protocol !== "https:" &&
                url.protocol !== "http:"
            )
            {
                return;
            }


            window.open(
                url.href,
                "_blank",
                "noopener,noreferrer"
            );

        }
        catch (error)
        {

            console.error(
                "Invalid Eva resource URL:",
                error
            );

        }

    }


    //----------------------------------------------------
    // Reset When Response Changes
    //----------------------------------------------------

    useEffect(
        () =>
        {

            closePdf();

            setDrawerOpen(false);

        },
        [response]
    );


    //----------------------------------------------------
    // Cleanup On Unmount
    //----------------------------------------------------

    useEffect(
        () =>
        {

            return () =>
            {

                releasePdf();

            };

        },
        []
    );


    //----------------------------------------------------
    // Escape Key
    //----------------------------------------------------

    useEffect(
        () =>
        {

            function handleKeyDown(event)
            {

                if (event.key !== "Escape")
                {
                    return;
                }

                if (selectedDocument)
                {

                    closePdf();

                }
                else if (drawerOpen)
                {

                    closeDrawer();

                }

            }


            window.addEventListener(
                "keydown",
                handleKeyDown
            );


            return () =>
            {

                window.removeEventListener(
                    "keydown",
                    handleKeyDown
                );

            };

        },
        [drawerOpen, selectedDocument]
    );


    //----------------------------------------------------
    // No Resources
    //----------------------------------------------------

    if (resourceCount === 0)
    {
        return null;
    }


    //====================================================
    // Render
    //====================================================

    return (

        <div className="eva-resource-library">

            {/*========================================
               Resource Indicator
            ========================================*/}

            <button
                type="button"
                className="eva-resource-indicator"
                onClick={() => setDrawerOpen(true)}
            >

                <span className="eva-resource-indicator-icon">
                    📚
                </span>

                <span className="eva-resource-indicator-text">
                    {resourceCount} Resources Found
                </span>

                <span className="eva-resource-indicator-arrow">
                    ›
                </span>

            </button>


            {/*========================================
               Resource Drawer
            ========================================*/}

            {drawerOpen && (

                <div className="eva-resource-overlay">

                    <div
                        className="eva-resource-backdrop"
                        onClick={closeDrawer}
                    />


                    <aside className="eva-resource-drawer">

                        {/*============================
                           Drawer Header
                        ============================*/}

                        <div className="eva-resource-drawer-header">

                            <div>

                                <div className="eva-resource-drawer-eyebrow">
                                    EVA EVIDENCE
                                </div>

                                <h2>
                                    Resource Library
                                </h2>

                                <p>
                                    {resourceCount} supporting resources
                                </p>

                            </div>


                            <button
                                type="button"
                                className="eva-resource-close"
                                onClick={closeDrawer}
                                aria-label="Close resource library"
                            >
                                ×
                            </button>

                        </div>


                        {/*============================
                           Documents
                        ============================*/}

                        {documentCount > 0 && (

                            <section className="eva-resource-section">

                                <h3>
                                    📄 Documents ({documentCount})
                                </h3>


                                {documentResources.map(
                                    document => (

                                        <div
                                            key={document.key}
                                            className="eva-resource-document"
                                        >

                                            <div className="eva-resource-document-title">

                                                {document.title}

                                            </div>


                                            {/*================
                                               Full Document
                                            ================*/}

                                            <button
                                                type="button"
                                                className="eva-resource-full-document"
                                                onClick={() =>
                                                    openFullDocument(
                                                        document
                                                    )
                                                }
                                            >
                                                📖 View Full Document
                                            </button>


                                            {/*================
                                               Matching Pages
                                            ================*/}

                                            <div className="eva-resource-document-pages">

                                                {document.pages.map(
                                                    (page, index) => (

                                                        <button
                                                            type="button"
                                                            key={
                                                                page.id ||
                                                                index
                                                            }
                                                            className="eva-resource-page"
                                                            onClick={() =>
                                                                openDocument(
                                                                    document,
                                                                    page
                                                                )
                                                            }
                                                        >

                                                            <span className="eva-resource-page-label">

                                                                Page {
                                                                    page.pageNumber ??
                                                                    "?"
                                                                }

                                                            </span>


                                                            {page.snippet && (

                                                                <span className="eva-resource-page-snippet">

                                                                    {page.snippet}

                                                                </span>

                                                            )}


                                                            <span className="eva-resource-page-arrow">
                                                                ↗
                                                            </span>

                                                        </button>

                                                    )
                                                )}

                                            </div>

                                        </div>

                                    )
                                )}

                            </section>

                        )}


                        {/*============================
                           Web Resources
                        ============================*/}

                        {webCount > 0 && (

                            <section className="eva-resource-section">

                                <h3>
                                    🌐 Web Resources ({webCount})
                                </h3>


                                {webResources.map(
                                    (resource, index) => (

                                        <button
                                            type="button"
                                            key={
                                                resource.id ||
                                                index
                                            }
                                            className="eva-resource-web"
                                            onClick={() =>
                                                openWebResource(
                                                    resource
                                                )
                                            }
                                        >

                                            <span className="eva-resource-web-title">

                                                {resource.title ||
                                                    "External Resource"}

                                            </span>


                                            {resource.snippet && (

                                                <span className="eva-resource-web-description">

                                                    {resource.snippet}

                                                </span>

                                            )}


                                            <span className="eva-resource-web-url">

                                                {resource.url}

                                            </span>

                                        </button>

                                    )
                                )}

                            </section>

                        )}

                    </aside>

                </div>

            )}


            {/*========================================
               PDF Modal

               Kept outside the drawer so it can
               overlay the entire Eva workspace.
            ========================================*/}

            {selectedDocument && (

                <div className="eva-resource-pdf-overlay">

                    <div className="eva-resource-pdf-modal">

                        {/*============================
                           PDF Header
                        ============================*/}

                        <div className="eva-resource-pdf-header">

                            <div className="eva-resource-pdf-heading">

                                <strong>
                                    {selectedDocument.title}
                                </strong>

                                <span>

                                    {selectedDocument.fullDocument
                                        ? "Complete Document"
                                        : "Page " +
                                            selectedDocument.pageNumber}

                                </span>

                            </div>


                            <button
                                type="button"
                                className="eva-resource-pdf-close"
                                onClick={closePdf}
                                aria-label="Close PDF viewer"
                            >
                                ✕
                            </button>

                        </div>


                        {/*============================
                           PDF Content
                        ============================*/}

                        <div className="eva-resource-pdf-content">

                            {pdfLoading && (

                                <div className="eva-resource-pdf-message">

                                    Loading document...

                                </div>

                            )}


                            {pdfError && (

                                <div className="eva-resource-pdf-message eva-resource-pdf-error">

                                    {pdfError}

                                </div>

                            )}


                            {pdfUrl && !pdfLoading && (

                                <iframe
                                    title={selectedDocument.title}
                                    src={
                                        pdfUrl +
                                        (
                                            selectedDocument.fullDocument
                                                ? ""
                                                : "#page=1"
                                        )
                                    }
                                    className="eva-resource-pdf-frame"
                                />

                            )}

                        </div>


                        {/*============================
                           PDF Footer
                        ============================*/}

                        <div className="eva-resource-pdf-footer">

                            {!selectedDocument.fullDocument && (

                                <span>
                                    Source page: {
                                        selectedDocument.pageNumber
                                    }
                                </span>

                            )}


                            <button
                                type="button"
                                onClick={closePdf}
                            >
                                Back to Resources
                            </button>

                        </div>

                    </div>

                </div>

            )}

        </div>

    );

}
